import type { LinkFailure } from './types';

export const GENERIC_LINK_FAILURE = "We couldn't add your payment method.";
export const CANCELLED_LINK_FAILURE =
  'You cancelled adding your payment method.';

const OUR_SIDE =
  'Something went wrong on our side. Please try again or pay another way.';

const REASONS: Array<[string, string[]]> = [
  [
    "Your card doesn't have enough funds or has reached its limit.",
    ['CC_51', 'CC_61', 'CC_65', 'insufficient_funds', 'card_velocity_exceeded']
  ],
  [
    'The card number, expiry date or CVV is incorrect.',
    [
      'P10',
      'CC_14',
      'CC_82',
      '3006',
      'incorrect_number',
      'incorrect_cvc',
      'invalid_expiry_month',
      'invalid_expiry_year'
    ]
  ],
  ['This card has expired.', ['CC_54', 'P53', '3005', 'expired_card']],
  [
    "Your bank couldn't confirm it's you. Please try again.",
    [
      '3000',
      '3001',
      '3009',
      'P3099',
      'authentication_required',
      'setup_intent_authentication_failure'
    ]
  ],
  [
    "This type of card isn't accepted. Try another card.",
    ['P50', 'P51', 'P55', 'P3011', 'card_not_supported']
  ],
  [
    "This card can't be used here. Try another card.",
    ['P58', 'P59', 'P60', 'P61', 'P62', '999']
  ],
  [
    'Your bank declined this card. Try another card or contact your bank.',
    [
      'CC_01',
      'CC_02',
      'CC_04',
      'CC_05',
      'CC_07',
      'CC_12',
      'CC_41',
      'CC_43',
      'CC_57',
      'CC_59',
      'CC_62',
      'CC_70',
      '3010',
      '3011',
      '3012',
      'card_declined',
      'generic_decline',
      'do_not_honor',
      'lost_card',
      'stolen_card',
      'fraudulent'
    ]
  ],
  [
    'The bank page timed out before you finished. Please try again.',
    ['P01', '01', '33', '99']
  ],
  [
    'Your bank is temporarily unavailable. Please try again in a few minutes.',
    [
      'P06',
      'P33',
      'P47',
      'P990',
      'E99',
      '3022',
      'CC_19',
      'CC_28',
      'processing_error'
    ]
  ],
  [
    OUR_SIDE,
    [
      'P02',
      'P03',
      'P04',
      'P05',
      'P07',
      'P13',
      'P16',
      'P403',
      'P404',
      'R01',
      'T01',
      'api_error'
    ]
  ]
];

const BY_CODE = new Map<string, string>(
  REASONS.flatMap(([sentence, codes]) =>
    codes.map((c): [string, string] => [c, sentence])
  )
);

const OUR_SIDE_PATTERN = /^M00(0[1-9]|[1-9][0-9])$/;

export function linkFailureReason(f: LinkFailure): {
  reason: string;
  code: string | null;
} {
  const code = f.code?.trim() || null;
  const desc = f.desc?.trim();
  if (desc) return { reason: desc, code };
  if (f.cancelled) return { reason: CANCELLED_LINK_FAILURE, code };
  if (code) {
    const mapped =
      BY_CODE.get(code) ?? (OUR_SIDE_PATTERN.test(code) ? OUR_SIDE : undefined);
    if (mapped) return { reason: mapped, code };
  }
  return { reason: GENERIC_LINK_FAILURE, code };
}

export function formatLinkFailure(f: LinkFailure): string {
  const { reason, code } = linkFailureReason(f);
  return code ? `${reason} (Code ${code})` : reason;
}
